import { Services } from '../../types/services';
import { AxiosError } from 'axios';

const CLIENT_PLUGIN_SETNOTES = 'CLIENT_PLUGIN_SETNOTES';

interface PluginConfiguration {
  pluginId: string;
  [key: string]: unknown;
}

interface PluginNote {
  pluginId: string;
  note: string;
}

interface ClientPlugin {
  pluginId: string;
  pluginVersion: string;
  [key: string]: unknown;
}

interface OperationResult {
  isSuccess: boolean;
  message: string;
}

interface State {
  pluginConfigurations: PluginConfiguration[];
  pluginNotes: PluginNote[];
}

const initialState: State = {
  pluginConfigurations: [],
  pluginNotes: [],
};

interface Action {
  type: typeof CLIENT_PLUGIN_SETNOTES;
  payload?: PluginNote;
}

export const reducer = (state = initialState, action: Action): State => {
  switch (action.type) {
    case CLIENT_PLUGIN_SETNOTES: {
      const notesData = action.payload as PluginNote;
      const pluginNotesData = [...state.pluginNotes];
      let actionData: PluginNote[];

      if (pluginNotesData.length <= 0) {
        actionData = [...state.pluginNotes, notesData];
      } else {
        const objIndex = pluginNotesData.findIndex((obj) => obj.pluginId === notesData.pluginId);

        if (objIndex !== -1) {
          pluginNotesData[objIndex] = { ...pluginNotesData[objIndex], note: notesData.note };
          actionData = pluginNotesData;
        } else {
          actionData = [...state.pluginNotes, notesData];
        }
      }

      return {
        ...state,
        pluginNotes: actionData,
      };
    }
    default:
      return state;
  }
};

export const actions = (services: Services) => {
  const axiosService = services.axios.Axios();

  const fetchClientManifest = (instanceId: string) => async () => {
    try {
      const res = await axiosService.get(`ClientManifest/${instanceId}`);
      return res.data;
    } catch (err) {
      console.error(err);
      return null;
    }
  };

  const fetchMissionControlStatus = (instanceId: string) => async () => {
    const res = await axiosService.get(`Instances/MissionControl/status?instanceId=${instanceId}`);
    return res.data;
  };

  const setPluginConfiguration = (pluginConfiguration: PluginConfiguration) => {
    return {
      type: 'CLIENT_PLUGIN_SETCONFIGURATIONS',
      payload: pluginConfiguration,
    };
  };

  const setPluginNote = (pluginNote: PluginNote) => {
    return {
      type: CLIENT_PLUGIN_SETNOTES,
      payload: pluginNote,
    };
  };

  const removeClientPlugin =
    (instanceId: string, pluginId: string, pluginVersion: string) => async (): Promise<OperationResult> => {
      try {
        await axiosService.delete(
          `ClientManifest/${instanceId}/client-plugin?pluginId=${pluginId}&pluginVersion=${pluginVersion}`
        );
        return { isSuccess: true, message: 'Remove Plugin succeeded!' };
      } catch (error) {
        return { isSuccess: false, message: 'Remove Plugin failed!' };
      }
    };

  const addClientPlugin =
    (instanceId: string, clientPlugin: ClientPlugin) => async (): Promise<OperationResult> => {
      try {
        const res = await axiosService.post(`ClientManifest/${instanceId}/client-plugin`, clientPlugin);
        if (res.status === 200) {
          return { isSuccess: true, message: 'Add Plugin succeeded!' };
        } else {
          return { isSuccess: false, message: 'Add Plugin failed!' };
        }
      } catch (error) {
        const axiosError = error as AxiosError<string>;
        let errorMsg = axiosError.response?.data || 'Add Plugin failed!';
        if (typeof errorMsg === 'string') {
          const errorMsgSplit = errorMsg.split('HEADERS');
          errorMsg = errorMsgSplit[0].trim();
        }
        return { isSuccess: false, message: errorMsg };
      }
    };

  const updateClientPlugin =
    (instanceId: string, updatedPlugin: ClientPlugin) => async (): Promise<OperationResult> => {
      try {
        await axiosService.put(`ClientManifest/${instanceId}/client-plugin`, updatedPlugin);
        return { isSuccess: true, message: 'Plugin update succeeded!' };
      } catch (error) {
        return { isSuccess: false, message: 'Plugin update failed!' };
      }
    };

  const triggerPushManifestToPractice = (instanceId: string) => async (): Promise<OperationResult> => {
    try {
      const res = await axiosService.post(`ClientManifest/client-plugin/pushmanifest/${instanceId}`);
      if (res.status === 200) {
        return { isSuccess: true, message: 'Push manifest succeeded!' };
      } else {
        return { isSuccess: false, message: 'Push manifest failed!' };
      }
    } catch (error) {
      console.error(error instanceof Error ? error.message : 'Error pushing manifest');
      return { isSuccess: false, message: 'Error pushing manifest' };
    }
  };

  const triggerInitiateStateCommand = (instanceId: string, pluginId: string, command = '') => {
    return async () => {
      try {
        await axiosService.post(
          `ClientManifest/client-plugin/${instanceId}/statecommand/${pluginId}?command=${command}`
        );
      } catch (error) {
        console.error(error instanceof Error ? error.message : 'Unknown error');
      }
    };
  };

  const updateMissionControlVersion = (instanceId: string, version: string) => {
    return async (): Promise<OperationResult> => {
      try {
        await axiosService.patch(`ClientManifest/${instanceId}/mission-control-version/${version}`);
        return { isSuccess: true, message: 'Mission Control version updated' };
      } catch (error) {
        return { isSuccess: false, message: 'Error updating Mission Control version' };
      }
    };
  };

  return {
    fetchClientManifest,
    fetchMissionControlStatus,
    setPluginConfiguration,
    setPluginNote,
    removeClientPlugin,
    addClientPlugin,
    updateClientPlugin,
    triggerPushManifestToPractice,
    triggerInitiateStateCommand,
    updateMissionControlVersion,
  };
};
